const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

class FocusTrapElement extends HTMLElement {
  static observedAttributes = ['active'];

  connectedCallback() {
    if (this.active) {
      this._activate();
    }
  }

  disconnectedCallback() {
    this._deactivate();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'active' || oldValue === newValue) {
      return;
    }

    if (newValue === null) {
      this._deactivate();
    } else {
      this._activate();
    }
  }

  get active() {
    return this.hasAttribute('active');
  }

  set active(value) {
    this.toggleAttribute('active', Boolean(value));
  }

  _activate() {
    if (this._controller) {
      return;
    }

    this._previouslyFocused = document.activeElement;
    this._controller = new AbortController();
    document.addEventListener('keydown', this._handleKeydown, {
      signal: this._controller.signal,
    });

    const focusable = this._focusableElements();
    const target = this.querySelector('[autofocus]') ?? focusable[0] ?? this;
    if (target === this && this.tabIndex < 0) {
      this.tabIndex = -1;
    }
    target.focus();
  }

  _deactivate() {
    if (!this._controller) {
      return;
    }

    this._controller.abort();
    this._controller = null;

    if (this._previouslyFocused instanceof HTMLElement) {
      this._previouslyFocused.focus();
    }
    this._previouslyFocused = null;
  }

  _handleKeydown = (event) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusable = this._focusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;

    if (event.shiftKey && (current === first || !this.contains(current))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    } else if (!this.contains(current)) {
      event.preventDefault();
      first.focus();
    }
  };

  _focusableElements() {
    return Array.from(this.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => element.offsetParent !== null || element === document.activeElement
    );
  }
}

customElements.define('chh-focus-trap', FocusTrapElement);

export default FocusTrapElement;
